import {
  Check,
  Pencil,
  Trash,
  Clipboard,
  ListChecks,
  Lightbulb,
} from "lucide-react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Button, Card, CardBody, Table } from "reactstrap"

import { usePermissions } from "hooks"
import { handleStopPropagation } from "components/Common/Utils"
import Paginations from "components/Common/Pagination"
import { useDispatch } from "react-redux"
import {
  getEvaluationQuestions,
  submitPracticalTrainingEvaluation,
  viewPracticalTrainingEvaluation,
} from "store/actions/practicalTrainingAction"
import { useSelector } from "react-redux"
import EvaluationModal from "./EvaluationModal"
import { showAlert } from "store/reducers/alertReducer"
import EvaluationResultModal from "./EvaluationResultModal"
import moment from "moment"
import NoDataAvailable from "components/Shared/NoDataAvailable"

const TableComponent = ({
  loading,
  data = [],
  onMarkAttendanceClick,
  onDeleteClick,
  itemIdToDelete,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  isLoading,
  trigger,
  payload,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { evaluationData, evaluationResult } = useSelector(
    state => state.PracticalTraining
  )
  const { permissions, showAll } = usePermissions("practical_training")

  const [showModal, setShowModal] = useState(false)
  const [showModalEvaluation, setShowModalEvaluation] = useState(false)
  const [selectedUuid, setSelectedUuid] = useState(null)

  const headers = [
    "S. No.",
    "Name",
    "Site",
    "Station",
    "Managed By",
    "Participants Count",
    "Attendance Count",
    "Start Date",
    "Start Time",
    "Duration",
    "Action",
  ]

  const hasTimePassed = (end_date, end_time) => {
    const endDateTime = moment(`${end_date} ${end_time}`, "DD-MM-YYYY h:mm A")
    return moment().isAfter(endDateTime)
  }

  const handleSubmitEvaluation = async data => {
    if (!data) return setShowModal(false)

    const { answers, comment } = data

    try {
      dispatch(
        submitPracticalTrainingEvaluation({
          training_uuid: selectedUuid,
          questions: answers,
          comment,
        })
      )
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            setShowModal(false)
            dispatch(showAlert({ text: message, type: "success" }))
            trigger()
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
      setShowModal(true)
    } catch (error) {
      console.log({ error })
    }
  }

  if (loading && data.length === 0) {
    return <NoDataAvailable />
  }

  return (
    <>
      <Card className="user_personal_info theme_card overflow-x-auto">
        <CardBody>
          <div className="table-responsive">
            <Table className="table mb-0">
              <thead className="table-light full_th">
                <tr>
                  {headers.map((header, index) => (
                    <th key={index}>{t(header)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index}>
                    <th scope="row">
                      {index + (payload?.limit || 50) * (currentPage - 1) + 1}
                    </th>
                    <td>
                      <p
                        className="text-decoration-underline cursor-pointer"
                        onClick={() => {
                          if (
                            showAll ||
                            permissions.includes("can_view_participants_lists")
                          ) {
                            navigate(
                              `/practical-training/participants?uuid=${item.uuid}`,
                              {
                                state: {
                                  end_date: item?.end_date,
                                  end_time: item?.end_time,
                                },
                              }
                            )
                          }
                        }}
                      >
                        {item.name}
                      </p>
                    </td>
                    <td>{item.site}</td>
                    <td>{item.station}</td>
                    <td>{item.managed_by}</td>
                    <td>{item.participants_count}</td>
                    <td>{item.attendance_count}</td>
                    <td>
                      <p className="ltr fit_content">{item.start_date}</p>
                    </td>
                    <td>
                      <p className="ltr fit_content">{item.start_time}</p>
                    </td>
                    <td>
                      {item.duration}{" "}
                      {item.duration == 1
                        ? t("Hour")
                        : item.duration > 1
                        ? t("Hours")
                        : ""}
                    </td>
                    <td onClick={handleStopPropagation}>
                      <div className="d-flex gap-2 action_buttons">
                        {/* {(showAll || permissions.includes("can_edit")) && ( */}
                        {item?.evaluated ? (
                          <Button
                            color="primary"
                            onClick={() => {
                              setSelectedUuid(item.uuid)
                              dispatch(
                                viewPracticalTrainingEvaluation({
                                  training_uuid: item.uuid,
                                })
                              ).then(() => setShowModalEvaluation(true))
                            }}
                            title={t("View Evaluation")}
                          >
                            <ListChecks />
                          </Button>
                        ) : (showAll ||
                            permissions.includes("can_submit_evaluation")) &&
                          hasTimePassed(item.end_date, item.end_time) ? (
                          <Button
                            color="primary"
                            onClick={() => {
                              setSelectedUuid(item.uuid)
                              dispatch(
                                getEvaluationQuestions({
                                  training_uuid: item.uuid,
                                })
                              ).then(() => setShowModal(true))
                            }}
                            title={t("Evaluate")}
                          >
                            <ListChecks />
                          </Button>
                        ) : null}
                        {(showAll ||
                          permissions.includes("can_view_promotion_lists")) && (
                          <Button
                            color="primary"
                            onClick={() => {
                              navigate(
                                `/practical-training/promotion-suggestions?uuid=${item.uuid}`
                              )
                            }}
                            title={t("Show Suggestions")}
                          >
                            <Lightbulb />
                          </Button>
                        )}

                        {/* )} */}
                        {(showAll || permissions.includes("can_edit")) && (
                          <Button
                            color="secondary"
                            onClick={() =>
                              navigate(
                                `/practical-training/update?id=${item.uuid}`
                              )
                            }
                            title={t("Update")}
                          >
                            <Pencil />
                          </Button>
                        )}
                        {(showAll || permissions.includes("can_create")) && (
                          <Button
                            color="secondary"
                            onClick={() => {
                              navigate(
                                `/practical-training/create?id=${item?.uuid}`
                              )
                            }}
                            disabled={
                              isLoading && item?.uuid === itemIdToDelete
                            }
                            title={t("Clone")}
                          >
                            <Clipboard />
                          </Button>
                        )}
                        {(showAll ||
                          permissions.includes("can_mark_attendance")) && (
                          <Button
                            color="primary"
                            onClick={() => onMarkAttendanceClick(item)}
                            title={t("Mark Attendance")}
                          >
                            <Check />
                          </Button>
                        )}
                        {(showAll || permissions.includes("can_delete")) && (
                          <Button
                            color="danger"
                            onClick={() => onDeleteClick(item)}
                            disabled={loading && item.uuid === itemIdToDelete}
                            title={t("Delete")}
                          >
                            <Trash />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      {!loading && data.length > 0 && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={onPageChange}
        />
      )}
      {showModal && (
        <EvaluationModal
          data={evaluationData}
          isOpen={showModal}
          toggle={handleSubmitEvaluation}
        />
      )}

      {showModalEvaluation && (
        <EvaluationResultModal
          isOpen={showModalEvaluation}
          toggle={() => setShowModalEvaluation(false)}
          data={evaluationResult}
        />
      )}
    </>
  )
}

export default TableComponent
